import { TabContext, TabPanel } from '@mui/lab';
import { Box, Checkbox, Divider, FormControlLabel, FormHelperText, Grid, InputAdornment, Tab, Tabs, TextField } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useRoundingRulesForm } from 'hooks/roundingRules/useRoundingRulesForm';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EntryMarking from './EntryMarking.tsx';
import ExitMarking from './ExitMarking';
import Omitted from './Omitted';
import Unplanned from './Unplanned';
import { RoundingRuleFormData } from 'repository/timekeeper/@types/RoundingRules';

interface Props {
    form: ReturnType<typeof useRoundingRulesForm>;
    mode: 'add' | 'edit' | 'delete' | 'info';
}

export const Form: React.FC<Props> = ({ form, mode }) => {
    const { t } = useTranslation();
    const { existName, form: formState } = form;
    const currentErrors = form.errors as unknown as Record<string, string | undefined>;
    const toTime = (value: string, fallback: string) => dayjs(value || fallback, 'HH:mm');

    const formData: RoundingRuleFormData = {
        name: formState.name,
        description: formState.description,
        markingTolerance: formState.markingTolerance,
        shiftTolerance: formState.shiftTolerance,
        checkRoundToEmployeeSchedule: formState.checkRoundToEmployeeSchedule,
        EntryEarlyChangePoint: toTime(formState.config_early_punch_in.change_point, '00:00'),
        EntryEarlyExternalRounding: toTime(formState.config_early_punch_in.rounding_extern, '00:01'),
        EntryEarlyInternalRounding: toTime(formState.config_early_punch_in.rounding_intern, '00:01'),
        EntryEarlyExternalGracePeriod: toTime(formState.config_early_punch_in.grace_period_extern, '00:00'),
        EntryEarlyInternalGracePeriod: toTime(formState.config_early_punch_in.grace_period_intern, '00:00'),
        EntryLateChangePoint: toTime(formState.config_late_punch_in.change_point, '00:00'),
        EntryLateExternalRounding: toTime(formState.config_late_punch_in.rounding_extern, '00:01'),
        EntryLateInternalRounding: toTime(formState.config_late_punch_in.rounding_intern, '00:01'),
        EntryLateExternalGracePeriod: toTime(formState.config_late_punch_in.grace_period_extern, '00:00'),
        EntryLateInternalGracePeriod: toTime(formState.config_late_punch_in.grace_period_intern, '00:00'),
        ExitEarlyChangePoint: toTime(formState.config_early_punch_out.change_point, '00:00'),
        ExitEarlyExternalRounding: toTime(formState.config_early_punch_out.rounding_extern, '00:01'),
        ExitEarlyInternalRounding: toTime(formState.config_early_punch_out.rounding_intern, '00:01'),
        ExitEarlyExternalGracePeriod: toTime(formState.config_early_punch_out.grace_period_extern, '00:00'),
        ExitEarlyInternalGracePeriod: toTime(formState.config_early_punch_out.grace_period_intern, '00:00'),
        ExitLateChangePoint: toTime(formState.config_late_punch_out.change_point, '00:00'),
        ExitLateExternalRounding: toTime(formState.config_late_punch_out.rounding_extern, '00:01'),
        ExitLateInternalRounding: toTime(formState.config_late_punch_out.rounding_intern, '00:01'),
        ExitLateExternalGracePeriod: toTime(formState.config_late_punch_out.grace_period_extern, '00:00'),
        ExitLateInternalGracePeriod: toTime(formState.config_late_punch_out.grace_period_intern, '00:00'),
        UnplannedEntryRounding: toTime(formState.config_unplan.rounding_entry, '00:01'),
        UnplannedEntryGracePeriod: toTime(formState.config_unplan.grace_period_entry, '00:00'),
        UnplannedExitRounding: toTime(formState.config_unplan.rounding_exit, '00:01'),
        UnplannedExitGracePeriod: toTime(formState.config_unplan.grace_period_exit, '00:00'),
        UnplannedTransferRounding: toTime(formState.config_unplan.rounding_trans, '00:01'),
        UnplannedTransferGracePeriod: toTime(formState.config_unplan.grace_period_trans, '00:00'),
        CheckUseScheduleTime: formState.config_omited_punch.planified,
        RadioUseScheduleTime: String(formState.config_omited_punch.type ?? 'totals_real_projected'),
        CheckTreatAsAnException: !!formState.config_omited_punch.is_exception,
    };

    const updateFormData = (field: keyof RoundingRuleFormData, value: string | number | boolean | Dayjs) => {
        switch (field) {
            case 'name':
            case 'description':
            case 'markingTolerance':
            case 'shiftTolerance':
            case 'checkRoundToEmployeeSchedule':
                form.update(field, value as never);
                return;
            case 'EntryEarlyChangePoint':
                form.update('config_early_punch_in', { ...formState.config_early_punch_in, change_point: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryEarlyExternalRounding':
                form.update('config_early_punch_in', { ...formState.config_early_punch_in, rounding_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryEarlyInternalRounding':
                form.update('config_early_punch_in', { ...formState.config_early_punch_in, rounding_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryEarlyExternalGracePeriod':
                form.update('config_early_punch_in', { ...formState.config_early_punch_in, grace_period_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryEarlyInternalGracePeriod':
                form.update('config_early_punch_in', { ...formState.config_early_punch_in, grace_period_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryLateChangePoint':
                form.update('config_late_punch_in', { ...formState.config_late_punch_in, change_point: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryLateExternalRounding':
                form.update('config_late_punch_in', { ...formState.config_late_punch_in, rounding_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryLateInternalRounding':
                form.update('config_late_punch_in', { ...formState.config_late_punch_in, rounding_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryLateExternalGracePeriod':
                form.update('config_late_punch_in', { ...formState.config_late_punch_in, grace_period_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'EntryLateInternalGracePeriod':
                form.update('config_late_punch_in', { ...formState.config_late_punch_in, grace_period_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitEarlyChangePoint':
                form.update('config_early_punch_out', { ...formState.config_early_punch_out, change_point: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitEarlyExternalRounding':
                form.update('config_early_punch_out', { ...formState.config_early_punch_out, rounding_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitEarlyInternalRounding':
                form.update('config_early_punch_out', { ...formState.config_early_punch_out, rounding_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitEarlyExternalGracePeriod':
                form.update('config_early_punch_out', { ...formState.config_early_punch_out, grace_period_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitEarlyInternalGracePeriod':
                form.update('config_early_punch_out', { ...formState.config_early_punch_out, grace_period_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitLateChangePoint':
                form.update('config_late_punch_out', { ...formState.config_late_punch_out, change_point: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitLateExternalRounding':
                form.update('config_late_punch_out', { ...formState.config_late_punch_out, rounding_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitLateInternalRounding':
                form.update('config_late_punch_out', { ...formState.config_late_punch_out, rounding_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitLateExternalGracePeriod':
                form.update('config_late_punch_out', { ...formState.config_late_punch_out, grace_period_extern: (value as Dayjs).format('HH:mm') });
                return;
            case 'ExitLateInternalGracePeriod':
                form.update('config_late_punch_out', { ...formState.config_late_punch_out, grace_period_intern: (value as Dayjs).format('HH:mm') });
                return;
            case 'UnplannedEntryRounding':
                form.update('config_unplan', { ...formState.config_unplan, rounding_entry: (value as Dayjs).format('HH:mm') });
                return;
            case 'UnplannedEntryGracePeriod':
                form.update('config_unplan', { ...formState.config_unplan, grace_period_entry: (value as Dayjs).format('HH:mm') });
                return;
            case 'UnplannedExitRounding':
                form.update('config_unplan', { ...formState.config_unplan, rounding_exit: (value as Dayjs).format('HH:mm') });
                return;
            case 'UnplannedExitGracePeriod':
                form.update('config_unplan', { ...formState.config_unplan, grace_period_exit: (value as Dayjs).format('HH:mm') });
                return;
            case 'UnplannedTransferRounding':
                form.update('config_unplan', { ...formState.config_unplan, rounding_trans: (value as Dayjs).format('HH:mm') });
                return;
            case 'UnplannedTransferGracePeriod':
                form.update('config_unplan', { ...formState.config_unplan, grace_period_trans: (value as Dayjs).format('HH:mm') });
                return;
            case 'CheckUseScheduleTime':
                form.update('config_omited_punch', { ...formState.config_omited_punch, planified: value as boolean });
                return;
            case 'RadioUseScheduleTime':
                form.update('config_omited_punch', { ...formState.config_omited_punch, type: value as string });
                return;
            case 'CheckTreatAsAnException':
                form.update('config_omited_punch', { ...formState.config_omited_punch, is_exception: value as boolean });
                return;
            default:
                return;
        }
    };

    const setErrors = React.useCallback<React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>>(
        (value) => {
            if (typeof value === 'function') {
                const nextErrors = value(currentErrors);
                form.setErrors(nextErrors as never);
                return;
            }
            form.setErrors(value as never);
        },
        [currentErrors, form],
    );
    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    label={t('settings.rounding_rule.table.modals.add.name')}
                    variant="outlined"
                    fullWidth
                    required={true}
                    autoFocus={true}
                    value={formState.name}
                    onChange={(e) => form.update('name', e.target.value)}
                    error={mode !== 'info' && existName(formState.name)}
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${formState.name?.length}/100`}</InputAdornment>,
                        },
                    }}
                    disabled={mode === 'info'}
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>
                        {mode !== 'info' && existName(formState.name) ? t('settings.fixed_rules.dialog.inputs.exist_name') : ''}
                    </Box>
                </FormHelperText>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    id="description"
                    name="description"
                    label={t('settings.paycodes.table.modals.add.description')}
                    variant="outlined"
                    multiline
                    rows={2}
                    value={formState.description}
                    onChange={(e) => form.update('description', e.target.value)}
                    fullWidth
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${formState.description?.length || 0}/255`}</InputAdornment>,
                        },
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControlLabel
                    disabled={true}
                    control={<Checkbox checked={formState.checkRoundToEmployeeSchedule} onChange={(event) => form.update('checkRoundToEmployeeSchedule', event.target.checked)} />}
                    label={t('settings.rounding_rule.table.modals.add.round_to_employee_hours')}
                />
            </Grid>
            {formState.checkRoundToEmployeeSchedule ? (
                <Grid container spacing={2} sx={{ p: 2 }}>
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TextField
                            label={t('settings.rounding_rule.table.modals.add.punch_tolerance')}
                            variant="outlined"
                            fullWidth
                            value={formState.markingTolerance}
                            onChange={(event) => form.update('markingTolerance', Number(event.target.value))}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TextField
                            label={t('settings.rounding_rule.table.modals.add.shift_tolerance')}
                            variant="outlined"
                            fullWidth
                            value={formState.shiftTolerance}
                            onChange={(event) => form.update('shiftTolerance', Number(event.target.value))}
                        />
                    </Grid>
                </Grid>
            ) : (
                <>
                    <Divider />

                    <TabContext value={form.value}>
                        <Tabs value={form.value} onChange={(event, value) => form.setValue(value)} variant="scrollable" scrollButtons="auto" aria-label="scrollable auto tabs example">
                            <Tab label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.title')} value={'entry'} />
                            <Tab label={t('settings.rounding_rule.table.modals.add.tabs.out_punch.title')} value={'exit'} />
                            <Tab label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.title')} value={'unplanned'} />
                            <Tab label={t('settings.rounding_rule.table.modals.add.tabs.omitted_punch.title')} value={'omitted'} />
                        </Tabs>

                        <Divider />

                        <TabPanel value={'entry'} sx={{ p: 0, mt: 1.5 }}>
                            <EntryMarking t={t} formData={formData} updateFormData={updateFormData} errors={currentErrors} setErrors={setErrors} loadingGettingRounding={form.loading} />
                        </TabPanel>

                        <TabPanel value={'exit'} sx={{ p: 0, mt: 1.5 }}>
                            <ExitMarking t={t} formData={formData} updateFormData={updateFormData} errors={currentErrors} setErrors={setErrors} loadingGettingRounding={form.loading} />
                        </TabPanel>

                        <TabPanel value={'unplanned'} sx={{ p: 0, mt: 1.5 }}>
                            <Unplanned t={t} formData={formData} updateFormData={updateFormData} errors={currentErrors} setErrors={setErrors} loadingGettingRounding={form.loading} />
                        </TabPanel>

                        <TabPanel value={'omitted'} sx={{ p: 0, mt: 1.5 }}>
                            <Omitted t={t} formData={formData} updateFormData={updateFormData} />
                        </TabPanel>
                    </TabContext>
                </>
            )}
        </Grid>
    );
};
